/**
 * The per-event frequency selector.
 *
 * A row is in one of two states and the control has to say which at a glance:
 *
 *  - **Default** — the row has no cadence of its own and tracks the tenant
 *    default. Rendered muted, prefixed "Default ·", so a screenful of rows
 *    reads as "these all follow the setting at the top".
 *  - **Custom** — the row was explicitly pinned. Rendered in full contrast
 *    with a Custom badge, and the per-row Reset puts it back to Default.
 *
 * Under the control sits one sentence saying what the row will actually do —
 * the cadence alone cannot say it, because the same cadence means "its own
 * email" or "a count in the shared summary" depending on which mail box is
 * ticked. `Off` is the one value that delivers no email at all, and says so
 * rather than leaving the reader to infer it. `Hourly` used to be the second
 * such value — selectable but collected by nothing — and now describes a real
 * delivery like the other batched cadences.
 */
import { Badge } from '../../../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '../../../ui/select';
import { BellOff, Mail } from 'lucide-react';
import {
  FREQUENCY_LABELS,
  INHERIT_FREQUENCY,
  type SetupChoice,
} from './types';
import type { NotificationFrequency } from '../../../../lib/api';

interface NotificationFrequencySelectProps {
  /** The row's own cadence, or `null` when it inherits. */
  value: NotificationFrequency | null;
  /**
   * What this row runs on while it inherits — the tenant default, or this
   * event's platform default when the tenant has set no blanket cadence.
   * Either way it is what the row will actually do, which is what the inherit
   * option has to say.
   */
  inheritedFrequency: NotificationFrequency;
  choices: SetupChoice[];
  disabled: boolean;
  /** Accessible name, e.g. `GRN dispute raised frequency`. */
  label: string;
  /** {@link describeDelivery} for this row's ticks and cadence. */
  deliveryHint: string;
  /** True when the pairing produces no email — shown with a struck-bell icon. */
  deliverySilent: boolean;
  onChange: (frequency: NotificationFrequency | null) => void;
}

export function NotificationFrequencySelect({
  value,
  inheritedFrequency,
  choices,
  disabled,
  label,
  deliveryHint,
  deliverySilent,
  onChange,
}: NotificationFrequencySelectProps) {
  const inherited = value === null;
  const defaultLabel = FREQUENCY_LABELS[inheritedFrequency] ?? inheritedFrequency;
  const HintIcon = deliverySilent ? BellOff : Mail;

  return (
    <div className="space-y-1">
      <Select
        value={inherited ? INHERIT_FREQUENCY : value}
        disabled={disabled}
        onValueChange={(next) =>
          onChange(next === INHERIT_FREQUENCY ? null : (next as NotificationFrequency))
        }
      >
        <SelectTrigger
          aria-label={label}
          className={`h-8 w-[190px] ${
            inherited ? 'border-dashed text-muted-foreground' : 'font-medium'
          }`}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={INHERIT_FREQUENCY}>
            Default · {defaultLabel}
          </SelectItem>
          <SelectSeparator />
          {choices.map((choice) => (
            <SelectItem key={choice.value} value={choice.value}>
              {choice.value === 'off' ? `${choice.label} — bell only` : choice.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="flex items-start gap-1.5">
        {!inherited && (
          <Badge variant="secondary" className="px-1.5 py-0 text-[10px] uppercase">
            Custom
          </Badge>
        )}
        <span className="flex items-start gap-1 text-xs text-muted-foreground">
          <HintIcon className="mt-0.5 h-3 w-3 shrink-0" />
          {deliveryHint}
        </span>
      </div>
    </div>
  );
}
